<script setup>
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { RoleCode } from '@/constants/accounts';
import { email, matches, required, strongPassword } from '@/utils/validation';

// Admin creates a parent account directly (approved, no OTP) and links the
// children. A parent's department comes from their children, so the JHS and
// SHS Admins link at least one from their own department (the server only
// offers those); the superadmin may leave it for the parent to do.
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const store = useStore();
const toast = useToast();
const childRequired = computed(() => store.getters.activeRoleCode !== RoleCode.Superadmin);

const formRef = ref(null);
const saving = shallowRef(false);
const reveal = shallowRef(false);
const emailTaken = shallowRef(false);

const blank = () => ({ fname: '', mname: '', lname: '', email: '', password: '', confirm: '', children: [] });
const form = reactive(blank());

watch(open, (isOpen) => {
  if (!isOpen) return;
  Object.assign(form, blank());
  emailTaken.value = false;
  reveal.value = false;
  results.value = [];
  formRef.value?.resetValidation();
});

async function checkEmail() {
  emailTaken.value = false;
  if (email(form.email) !== true || !form.email) return;
  try {
    emailTaken.value = !!(await apiRequest('get', `/auth/checkEmailIfExist/${encodeURIComponent(form.email)}`));
  } catch {
    emailTaken.value = false;
  }
}

// Student search, by name or LRN. The chosen children stay in the list so
// their chips keep showing while a new search runs.
const results = shallowRef([]);
const searching = shallowRef(false);
let searchTimer;
let searchSeq = 0;
function onSearch(term) {
  clearTimeout(searchTimer);
  const q = String(term ?? '').trim();
  if (q.length < 2) {
    results.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    const seq = ++searchSeq;
    searching.value = true;
    try {
      const rows = await apiRequest('get', `/auth/linkableStudents?q=${encodeURIComponent(q)}`);
      if (seq === searchSeq) results.value = Array.isArray(rows) ? rows : [];
    } catch {
      if (seq === searchSeq) results.value = [];
    } finally {
      if (seq === searchSeq) searching.value = false;
    }
  }, 300);
}
const studentItems = computed(() => {
  const picked = new Set(form.children.map((c) => c.id));
  return [...form.children, ...results.value.filter((r) => !picked.has(r.id))];
});

const childRules = computed(() => (childRequired.value ? [(v) => (v && v.length > 0) || 'Link at least one child.'] : []));

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid || emailTaken.value) return;
  saving.value = true;
  try {
    const body = await apiRequest('post', '/auth/addParentByAdmin', {
      fname: form.fname.trim(),
      mname: form.mname.trim(),
      lname: form.lname.trim(),
      email: form.email.trim(),
      password: form.password,
      studentIDs: form.children.map((c) => Number(c.id)),
      schoolYearID: Number(store.getters.getFilterSelected),
    });
    assertOk(body, [200, 201]);
    toast.success(body.msg || 'Parent account created.');
    open.value = false;
    emit('saved');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not create the account.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Add parent account"
    description="Create a parent or guardian account linked to their children. It's approved right away. Share the password with them; they'll choose their own when they first sign in."
    size="lg"
  >
    <v-form ref="formRef" class="form-grid" @submit.prevent="save">
      <UiFormField label="First name" required v-slot="{ id }">
        <v-text-field :id="id" v-model="form.fname" :rules="[required]" hide-details="auto" />
      </UiFormField>
      <UiFormField label="Last name" required v-slot="{ id }">
        <v-text-field :id="id" v-model="form.lname" :rules="[required]" hide-details="auto" />
      </UiFormField>
      <UiFormField label="Middle name" v-slot="{ id }">
        <v-text-field :id="id" v-model="form.mname" hide-details="auto" />
      </UiFormField>
      <UiFormField label="Email" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.email"
          type="email"
          :rules="[required, email]"
          :error-messages="emailTaken ? 'This email is already in use.' : []"
          hide-details="auto"
          autocomplete="off"
          @blur="checkEmail"
          @update:model-value="emailTaken = false"
        />
      </UiFormField>

      <UiFormField label="Password" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.password"
          :type="reveal ? 'text' : 'password'"
          :append-inner-icon="reveal ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :rules="[required, strongPassword]"
          hide-details="auto"
          autocomplete="new-password"
          @click:append-inner="reveal = !reveal"
        />
      </UiFormField>
      <UiFormField label="Confirm password" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.confirm"
          :type="reveal ? 'text' : 'password'"
          :rules="[required, matches(() => form.password, 'Passwords do not match.')]"
          hide-details="auto"
          autocomplete="new-password"
        />
      </UiFormField>

      <UiFormField
        label="Children"
        :description="
          childRequired
            ? 'Search by name or LRN. Only students of your department without a parent account are listed.'
            : 'Search by name or LRN. Optional; the parent can also link their children after signing in.'
        "
        :required="childRequired"
        class="span-2"
        v-slot="{ id }"
      >
        <v-autocomplete
          :id="id"
          v-model="form.children"
          :items="studentItems"
          :loading="searching"
          :rules="childRules"
          item-value="id"
          item-title="name"
          return-object
          multiple
          chips
          closable-chips
          no-filter
          placeholder="Type a name or LRN"
          no-data-text="Type at least 2 letters to search."
          hide-details="auto"
          @update:search="onSearch"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps" :subtitle="`${item.raw.grade || 'No grade'} · LRN ${item.raw.lrn || '—'}`" />
          </template>
        </v-autocomplete>
      </UiFormField>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" @click="save">Create account</v-btn>
    </template>
  </UiDialog>
</template>
